import { ArrowLeft, FileText, Pin } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { get, toQuery } from '../api';
import { Empty, ErrorBox, Loading } from '../components/base';
import { Markdown } from '../components/Markdown';
import { CATEGORY_LABELS, formatDate } from '../formats';
import type { DocumentFull, DocumentSummary } from '../types';

const CATEGORIES = ['', 'update', 'report', 'guide', 'info'];

export function DocumentList() {
  const [documents, setDocuments] = useState<DocumentSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [category, setCategory] = useState('');

  useEffect(() => {
    setDocuments(null);
    get<{ documents: DocumentSummary[] }>(`/documents${toQuery({ category })}`)
      .then(({ documents: d }) => setDocuments(d))
      .catch((e) => setError(e instanceof Error ? e.message : 'Failed to load.'));
  }, [category]);

  if (error) return <ErrorBox message={error} />;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-semibold">Documents</h1>
        <div className="flex rounded-lg border border-paper-300 dark:border-ink-700" role="group" aria-label="Category">
          {CATEGORIES.map((c) => (
            <button
              key={c}
              type="button"
              onClick={() => setCategory(c)}
              className={`px-3 py-1.5 text-xs font-medium first:rounded-l-lg last:rounded-r-lg ${
                category === c
                  ? 'bg-ink-900 text-paper-100 dark:bg-paper-100 dark:text-ink-900'
                  : 'text-ink-600 hover:bg-paper-100 dark:text-paper-300 dark:hover:bg-ink-700'
              }`}
            >
              {c === '' ? 'All' : CATEGORY_LABELS[c]}
            </button>
          ))}
        </div>
      </div>

      {documents === null ? (
        <Loading />
      ) : documents.length === 0 ? (
        <div className="card">
          <Empty
            title="Nothing here yet"
            description="Updates, reports and guides about your engagement will appear here."
          />
        </div>
      ) : (
        <div className="card divide-y divide-paper-200 dark:divide-ink-700">
          {documents.map((d) => (
            <Link
              key={d.id}
              to={`/documents/${d.id}`}
              className="flex items-center gap-3 px-4 py-3.5 hover:bg-paper-50 dark:hover:bg-ink-700/50"
            >
              <FileText className="h-4 w-4 shrink-0 text-ink-400" aria-hidden />
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium">{d.title}</p>
                <p className="text-xs text-ink-500 dark:text-ink-400">
                  {CATEGORY_LABELS[d.category]}
                  {d.product_name ? ` · ${d.product_name}` : ''} · {formatDate(d.updated_at)}
                </p>
              </div>
              {d.pinned === 1 && <Pin className="h-3.5 w-3.5 shrink-0 text-brand-600" aria-hidden />}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

export function DocumentView() {
  const { id } = useParams();
  const [doc, setDoc] = useState<DocumentFull | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setDoc(null);
    get<{ document: DocumentFull }>(`/documents/${id}`)
      .then(({ document: d }) => setDoc(d))
      .catch((e) => setError(e instanceof Error ? e.message : 'Failed to load.'));
  }, [id]);

  if (error) return <ErrorBox message={error} />;
  if (!doc) return <Loading />;

  return (
    <div className="mx-auto max-w-3xl space-y-4">
      <Link
        to="/documents"
        className="inline-flex items-center gap-1.5 text-sm font-medium text-ink-500
                   hover:text-ink-700 dark:text-ink-400 dark:hover:text-paper-200"
      >
        <ArrowLeft className="h-4 w-4" aria-hidden /> All documents
      </Link>

      <article className="card p-6 sm:p-8">
        <header className="mb-5 border-b border-paper-200 pb-5 dark:border-ink-700">
          <p className="text-xs font-medium uppercase tracking-wide text-brand-700 dark:text-brand-400">
            {CATEGORY_LABELS[doc.category]}
            {doc.product_name ? ` · ${doc.product_name}` : ''}
          </p>
          <h1 className="mt-1 text-2xl font-semibold">{doc.title}</h1>
          <p className="mt-1 text-xs text-ink-500 dark:text-ink-400">
            Updated {formatDate(doc.updated_at)}
          </p>
        </header>
        <Markdown source={doc.body} />
      </article>
    </div>
  );
}
